import {Box, Typography} from '@mui/material';

import {orangeColors} from '../../layouts/theme/designSystem';
import {getScoreLevelColors} from '../../utils/getScoreLevelColors';

export interface Props {
  value: number;
  label: string;
  onClick?: () => void;
  active?: boolean;
}

const CIRCLE_SIZE = 72;
const CIRCLE_CENTER = CIRCLE_SIZE / 2;
const CIRCLE_RADIUS = 27;
const CIRCLE_STROKE = 5;

export const ScoreCircle = ({value, label, onClick, active}: Props) => {
  const {strokeColor} = getScoreLevelColors(value);
  const circumference = 2 * Math.PI * CIRCLE_RADIUS;
  const dash = (value / 100) * circumference;
  const isClickable = !!onClick;

  return (
    <Box
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick?.();
            }
          }
          : undefined
      }
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
        ...(isClickable && {
          cursor: 'pointer',
          borderRadius: '10px',
          px: 1,
          py: 0.75,
          border: '1px solid transparent',
          transition: 'all 0.15s',
          '&:hover': {bgcolor: 'action.hover'},
        }),
        ...(active && {
          bgcolor: orangeColors[4],
          borderColor: orangeColors[5],
        }),
      }}
    >
      <Box sx={{position: 'relative', width: CIRCLE_SIZE, height: CIRCLE_SIZE}}>
        <svg
          viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`}
          width={CIRCLE_SIZE}
          height={CIRCLE_SIZE}
          style={{transform: 'rotate(-90deg)'}}
        >
          <circle
            cx={CIRCLE_CENTER}
            cy={CIRCLE_CENTER}
            r={CIRCLE_RADIUS}
            fill='none'
            stroke={orangeColors[5]}
            strokeWidth={CIRCLE_STROKE}
          />
          <circle
            cx={CIRCLE_CENTER}
            cy={CIRCLE_CENTER}
            r={CIRCLE_RADIUS}
            fill='none'
            stroke={strokeColor}
            strokeWidth={CIRCLE_STROKE}
            strokeDasharray={`${dash} ${circumference}`}
            strokeLinecap='round'
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography sx={{fontSize: 16, fontWeight: 700, lineHeight: 1}}>{value}%</Typography>
        </Box>
      </Box>
      <Typography sx={{fontSize: 11, color: 'text.secondary', textAlign: 'center', maxWidth: CIRCLE_SIZE}}>
        {label}
      </Typography>
    </Box>
  );
};
